import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useConfirm } from "./Confirm";

interface Comment {
  id: string;
  author_entra_oid: string;
  author_email: string;
  author_name: string | null;
  body: string;
  created_at: string;
  deleted_at: string | null;
  deleted_by_email: string | null;
}

function initials(name: string | null | undefined, email: string): string {
  const src = (name && name.trim()) || email;
  const parts = src.split(/[\s.@]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
}

export function CommentThread({ estimateId, currentOid, isAdmin }: { estimateId: string; currentOid: string; isAdmin: boolean }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({
    queryKey: ["comments", estimateId],
    queryFn: () => api.commentsList(estimateId),
  });
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const comments: Comment[] = data?.comments ?? [];

  async function post() {
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    try {
      await api.commentCreate(estimateId, text);
      setDraft("");
      await qc.invalidateQueries({ queryKey: ["comments", estimateId] });
    } finally {
      setPosting(false);
    }
  }

  async function remove(c: Comment) {
    const ok = await confirm({
      title: "Delete comment?",
      body: "This will hide the comment from the thread. The audit log keeps a record.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    await api.commentDelete(estimateId, c.id);
    await qc.invalidateQueries({ queryKey: ["comments", estimateId] });
  }

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
      <h2 className="text-lg font-semibold text-liaise-navy mb-4">Comments</h2>

      {isLoading ? (
        <div className="text-sm text-slate-500">Loading comments...</div>
      ) : comments.length === 0 ? (
        <div className="text-sm text-slate-500 italic">No comments yet. Be the first to leave a note.</div>
      ) : (
        <ul className="space-y-4 mb-4">
          {comments.map((c) => {
            const canDelete = !c.deleted_at && (c.author_entra_oid === currentOid || isAdmin);
            return (
              <li key={c.id} className="flex gap-3">
                <div className="w-9 h-9 rounded-full bg-liaise-blue/15 text-liaise-blue flex items-center justify-center text-xs font-semibold shrink-0">
                  {initials(c.author_name, c.author_email)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm">
                      <span className="font-medium text-liaise-navy">{c.author_name || c.author_email}</span>
                      <span className="text-xs text-slate-500 ml-2">{formatWhen(c.created_at)}</span>
                    </div>
                    {canDelete && (
                      <button onClick={() => remove(c)}
                        className="text-xs text-slate-400 hover:text-liaise-red">
                        Delete
                      </button>
                    )}
                  </div>
                  {c.deleted_at ? (
                    <div className="text-sm text-slate-400 italic mt-1">Comment deleted by {c.deleted_by_email}</div>
                  ) : (
                    <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words">{c.body}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Please include relevant information for the costing, such as existing 1:1 support already funded."
          rows={3}
          className="w-full px-3 py-2 border border-slate-200 rounded text-sm"
        />
        <div className="mt-2 flex justify-between items-center">
          <div className="text-xs text-slate-400">{draft.length} / 4000</div>
          <button
            onClick={post}
            disabled={!draft.trim() || posting || draft.length > 4000}
            className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium disabled:opacity-50"
          >
            {posting ? "Posting..." : "Post comment"}
          </button>
        </div>
      </div>
    </section>
  );
}
